/**
 * The raster renderer, which is the default.
 *
 * The page draws the slip to a canvas with the font the application already
 * ships and sends the printer a picture of it. Slower, larger, and the only
 * mode that prints Ө, Ү, ө and ү — see `escpos.ts` for why that is not a
 * preference.
 *
 * Drawing happens *here*, in the page, not in Rust. The Electron app rendered
 * receipts in the main process with `@napi-rs/canvas`, registering four TTFs
 * from a path with a dev branch, a packaged branch and a backwards-compatibility
 * fallback — so the person changing a receipt's layout could not see it.
 */
import { concatBytes, row } from './escpos'
import { encodeQr } from './qr'
import type { Slip } from './slip-model'

/**
 * The face the design is set in: Manrope, Medium for the body and ExtraBold for
 * the emphasis. The variable build is what the kiosk ships (`@fontsource-
 * variable/manrope`), and it carries Ө and Ү — checked, because a face without
 * them is a raster receipt that folds them exactly the way text mode does.
 */
export const RECEIPT_FONT = "'Manrope Variable', Manrope, system-ui, sans-serif"

/** Medium and ExtraBold, the design's two weights. */
const REGULAR = 500
const BOLD = 800

export interface RasterOptions {
  /** Printable width in dots. 8 dots/mm, so 80mm prints 72mm = 576. */
  readonly dots: number
  readonly fontFamily?: string
  readonly baseSize?: number
  /**
   * Logos, decoded, keyed by the data URL the block carries.
   *
   * Decoding is asynchronous and drawing is not, so it happens above this file —
   * `apps/kiosk/src/shell/print.ts` decodes once and caches. A block whose image
   * is missing from this map still reserves its height and prints blank, which
   * is the right failure: the alternative shifts every line below it, and a
   * receipt whose totals moved is worse than one with a gap.
   */
  readonly images?: ReadonlyMap<string, DecodedImage>
}

/**
 * A decoded image, narrowed the way `Canvas2D` is.
 *
 * Not `CanvasImageSource`: that is a `lib.dom` type and this file imports none,
 * so that a test — and a server-side renderer, one day — can supply one.
 */
export interface DecodedImage {
  readonly width: number
  readonly height: number
}

/** A 2D context, narrowed to what this file uses, so a test can supply one. */
export interface Canvas2D {
  canvas: { width: number; height: number }
  fillStyle: string | object
  font: string
  /** Widened to the DOM's own union so a real 2D context satisfies this. */
  textAlign: 'left' | 'center' | 'right' | 'start' | 'end'
  textBaseline: 'top' | 'alphabetic' | 'bottom' | 'hanging' | 'ideographic' | 'middle'
  fillRect(x: number, y: number, w: number, h: number): void
  fillText(text: string, x: number, y: number): void
  measureText(text: string): { width: number }
  /**
   * Optional, and that is load-bearing twice over.
   *
   * `receipt.test.ts` builds a *typed literal* `Canvas2D`, so a required method
   * here would be a compile error in the test rather than a missing feature —
   * and a context without one simply skips the logo.
   *
   * `image` is `unknown` rather than `DecodedImage` because a real
   * `CanvasRenderingContext2D` has to satisfy this interface, and the DOM's own
   * `drawImage` takes a `CanvasImageSource` — a union including `SVGImageElement`
   * and `VideoFrame`, which no structural type of ours is compatible with in
   * either direction. `unknown` is the one parameter type the DOM's method is
   * assignable to. What actually goes in is a `DecodedImage`, which is what the
   * caller's map is typed as.
   */
  drawImage?(image: unknown, x: number, y: number, w: number, h: number): void
  getImageData(x: number, y: number, w: number, h: number): { data: Uint8ClampedArray }
}

export type CanvasFactory = (width: number, height: number) => { context: Canvas2D; done: () => void }

/**
 * How wide one QR module is drawn, in printer dots.
 *
 * About 45% of the paper for a short payload, but **never below four dots a
 * module** — 0.5mm at 203dpi, roughly where the head's dot gain stops closing
 * modules up and a phone camera still resolves them at arm's length. The floor
 * is what makes this grow with the payload instead of holding one width and
 * thinning the modules to fit: a longer ДДТД is a higher version is more
 * modules, and at a fixed 40% a version 10 code came out with 3-dot modules
 * *and a shorter slip than a version 1 one*, which is how the height
 * calculation and the drawing disagreed.
 */
export const qrScale = (modules: number, dots: number): number => {
  const span = modules + 8
  const fits = Math.max(1, Math.floor(dots / span))
  return Math.min(fits, Math.max(4, Math.floor((dots * 0.45) / span)))
}

/**
 * How tall a logo prints, on a head that may be narrower than the one it was
 * prepared for.
 *
 * **Never upscaled.** The image arrives already dithered for `sourceDots`, and a
 * one-bit dither resampled by a fraction of a dot is mud — the same argument
 * `qrScale` makes about modules. A 576-dot logo on a 384-dot head is drawn at
 * 384 and keeps its aspect; a 384-dot logo on a 576-dot head is drawn at 384
 * rather than blown up.
 */
export function logoHeight(sourceDots: number, sourceHeight: number, dots: number): number {
  const width = Math.min(sourceDots, dots)
  return Math.round((sourceHeight * width) / sourceDots)
}

/** How wide a string sets at a size and weight. */
export type Measure = (text: string, px: number, weight: number) => number

/**
 * A width for when there is no font to ask: a little over half an em a
 * character, which is Manrope's average and errs wide. Only `measureHeight`
 * uses it — `drawSlip` measures with the context it draws on, so the canvas it
 * allocates is the height it fills.
 */
const estimate: Measure = (text, px) => [...text].length * px * 0.58

/** What {@link layout} does with a position once it has one. */
interface Ink {
  text(text: string, x: number, y: number, align: 'left' | 'center' | 'right', px: number, weight: number): void
  rect(x: number, y: number, w: number, h: number): void
  image(data: string, x: number, y: number, w: number, h: number): void
}

/** Break at spaces to fit `max`, and inside a word only when one word will not. */
function wrap(text: string, fits: (text: string) => boolean): string[] {
  const out: string[] = []
  // Leading space is an indent — an add-on under its drink — and splitting on
  // spaces would otherwise eat it.
  const body = text.trimStart()
  let line = text.slice(0, text.length - body.length)
  const lead = line
  for (const word of body.split(' ')) {
    const tried = line === lead ? `${line}${word}` : `${line} ${word}`
    if (fits(tried)) {
      line = tried
      continue
    }
    if (line.length > 0) out.push(line)
    line = word
    // A 33-digit ДДТД has no spaces to break at.
    while (!fits(line) && line.length > 1) {
      let cut = line.length - 1
      while (cut > 1 && !fits(line.slice(0, cut))) cut -= 1
      out.push(line.slice(0, cut))
      line = line.slice(cut)
    }
  }
  out.push(line)
  return out
}

/** Shorten with an ellipsis until it fits, for a cell that must stay one line. */
function clip(text: string, fits: (text: string) => boolean): string {
  if (fits(text)) return text
  let chars = [...text]
  while (chars.length > 0 && !fits(`${chars.join('')}…`)) chars = chars.slice(0, -1)
  return `${chars.join('')}…`
}

/**
 * Where everything goes, and how tall that makes the slip.
 *
 * **One walk for both jobs.** `measureHeight` and `drawSlip` used to be two
 * switches over the same union, and they disagreed — over QR versions, once —
 * which is a code drawn over whatever followed it. Now the drawing is this walk
 * with `ink` supplied and the measuring is the same walk without it, so the
 * height allocated is by construction the height filled.
 */
function layout(slip: Slip, base: number, width: number, measure: Measure, ink?: Ink): number {
  const small = Math.round(base * 0.75)
  const large = base * 2
  const line = Math.round(base * 1.75)
  const fitsIn = (max: number, px: number, weight: number) => (text: string) => measure(text, px, weight) <= max
  /** The size a line sets at so it fits, from `px` down. */
  const shrink = (text: string, px: number, weight: number, max: number) => {
    const at = measure(text, px, weight)
    return at <= max ? px : Math.max(small, Math.floor((px * max) / at))
  }

  let y = base

  for (const block of slip.blocks) {
    switch (block.kind) {
      case 'text': {
        const px = block.size === 'large' ? Math.round(base * 1.6) : block.size === 'small' ? small : base
        const weight = block.bold === true ? BOLD : REGULAR
        const align = block.align ?? 'left'
        const x = align === 'center' ? width / 2 : align === 'right' ? width : 0
        for (const piece of wrap(block.text, fitsIn(width, px, weight))) {
          ink?.text(piece, x, y, align, px, weight)
          y += Math.round(px * 1.5)
        }
        y += Math.round(px * 0.25)
        break
      }
      case 'row': {
        // Measuring both halves and drawing them at the two margins is exact
        // whatever the font is; the label gives way when they would touch.
        const weight = block.bold === true ? BOLD : REGULAR
        const indent = (block.indent ?? 0) * base * 0.6
        const right = measure(block.right, base, weight)
        const left = clip(block.left, fitsIn(Math.max(0, width - indent - right - base), base, weight))
        ink?.text(left, indent, y, 'left', base, weight)
        ink?.text(block.right, width, y, 'right', base, weight)
        y += line
        break
      }
      case 'columns': {
        // The design's columns: a quantity centred at two thirds, a unit price
        // ending at 85%, the amount at the margin. The name wraps inside the
        // space left of the quantity rather than pushing the numbers along.
        const quantityAt = Math.round(width * 0.66)
        const unitEnd = Math.round(width * 0.85)
        const nameMax = quantityAt - base * 2
        const [name, quantity, unit, amount] = block.cells
        const names = wrap(name, fitsIn(nameMax, base, REGULAR))
        ink?.text(quantity, quantityAt, y, 'center', base, REGULAR)
        ink?.text(unit, unitEnd, y, 'right', base, REGULAR)
        ink?.text(amount, width, y, 'right', base, REGULAR)
        for (const [at, piece] of names.entries())
          ink?.text(piece, 0, y + at * Math.round(base * 1.3), 'left', base, REGULAR)
        y += line + (names.length - 1) * Math.round(base * 1.3)
        break
      }
      case 'rule':
        ink?.rect(0, y + Math.round(base * 0.35), width, Math.max(2, Math.round(base / 9)))
        y += base
        break
      case 'space':
        y += base * (block.lines ?? 1)
        break
      case 'number': {
        const text = block.label === undefined ? block.value : `${block.label}: ${block.value}`
        const px = shrink(text, large, BOLD, width)
        ink?.text(text, width / 2, y, 'center', px, BOLD)
        y += Math.round(px * 1.6)
        break
      }
      case 'qr': {
        // Drawn module by module rather than scaled from a smaller bitmap: a
        // QR resampled by a fraction of a dot is a QR with ragged module edges,
        // which is exactly what a scanner fails on.
        const span = drawQr(block.data, width, (width - qrSpan(block.data, width)) / 2, y, ink)
        y += span + Math.round(base * 0.5)
        if (block.caption !== undefined) {
          ink?.text(block.caption, width / 2, y, 'center', small, REGULAR)
          y += Math.round(small * 1.5)
        }
        break
      }
      case 'barcode':
        ink?.text(block.data, width / 2, y, 'center', base, REGULAR)
        y += line
        break
      case 'header': {
        const lineHeight = Math.round(small * 1.35)
        const widest = Math.max(0, ...block.lines.map((line) => measure(line, small, BOLD)))
        const drawn = block.image === null ? 0 : Math.min(block.image.dots, width)
        const tall = block.image === null ? 0 : logoHeight(block.image.dots, block.image.height, width)
        const text = block.lines.length * lineHeight
        const beside = block.image === null || drawn + base + widest <= width
        const paint = (x: number, top: number) => {
          if (block.image === null) return
          ink?.image(block.image.data, x, top, drawn, tall)
        }
        if (beside) {
          const height = Math.max(tall, text)
          paint(0, y + Math.round((height - tall) / 2))
          const top = y + Math.round((height - text) / 2)
          const align = block.image === null ? 'center' : 'right'
          for (const [at, piece] of block.lines.entries()) {
            ink?.text(piece, align === 'center' ? width / 2 : width, top + at * lineHeight, align, small, BOLD)
          }
          y += height
        } else {
          // A head too narrow for both: the art centred, the lines under it.
          paint(Math.round((width - drawn) / 2), y)
          y += tall + Math.round(base * 0.4)
          for (const [at, piece] of block.lines.entries())
            ink?.text(piece, width / 2, y + at * lineHeight, 'center', small, BOLD)
          y += text
        }
        y += Math.round(base * 0.5)
        break
      }
      case 'fiscalPanel': {
        const span = block.qr === null ? 0 : qrSpan(block.qr, width)
        const column = width - span - base
        // Beside the code when there is room for a readable number there, and
        // under it on a head too narrow for both.
        const beside = block.qr === null || column >= width * 0.3
        const left = block.qr === null ? 0 : beside ? span + base : 0
        const room = beside ? width - left : width
        const align = beside ? 'left' : 'center'
        const x = beside ? left : width / 2

        const sizes = block.items.map((item) => shrink(item.value, Math.round(base * 1.9), BOLD, room))
        const labels = block.items.map((item) => shrink(item.label, base, BOLD, room))
        const itemHeight = (at: number) => Math.round(base * 1.35) + Math.round(sizes[at]! * 1.3)
        const gap = base
        const items =
          block.items.reduce((total, _item, at) => total + itemHeight(at), 0) + gap * (block.items.length - 1)

        let top: number
        if (block.qr !== null && beside) {
          const height = Math.max(span, items)
          drawQr(block.qr, width, 0, y + Math.round((height - span) / 2), ink)
          top = y + Math.round((height - items) / 2)
          y += height
        } else {
          if (block.qr !== null) {
            drawQr(block.qr, width, (width - span) / 2, y, ink)
            y += span
          }
          top = y
          y += items
        }
        block.items.forEach((item, at) => {
          ink?.text(item.label, x, top, align, labels[at]!, BOLD)
          ink?.text(item.value, x, top + Math.round(base * 1.35), align, sizes[at]!, BOLD)
          top += itemHeight(at) + gap
        })
        y += Math.round(base * 0.5)
        break
      }
      default: {
        // The union is closed, and this binding is what makes adding a block
        // kind a compile error here rather than a slip that measures one height
        // and draws another.
        const unhandled: never = block
        void unhandled
      }
    }
  }
  return Math.ceil(y + base * 2)
}

/** The drawn width — and height — of a QR, quiet zone included. */
function qrSpan(data: string, width: number): number {
  const { size } = encodeQr(data)
  return (size + 8) * qrScale(size, width)
}

/** Draw a QR with its top-left (quiet zone included) at `x, y`; returns its span. */
function drawQr(data: string, width: number, x: number, y: number, ink: Ink | undefined): number {
  const { size, modules } = encodeQr(data)
  const scale = qrScale(size, width)
  if (ink !== undefined) {
    const left = Math.round(x)
    // The quiet zone is white paper, so only the dark modules are drawn.
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (modules[r * size + c] !== true) continue
        ink.rect(left + (c + 4) * scale, y + (r + 4) * scale, scale, scale)
      }
    }
  }
  return (size + 8) * scale
}

/** The body size for a head: 6pt on 80mm, and never below what a head resolves. */
export const baseSizeFor = (dots: number): number => Math.max(16, Math.round(dots / 32))

/**
 * How tall the slip will be, without a canvas to measure on.
 *
 * `dots` is here because a QR's height is a property of its payload — a longer
 * ДДТД is a higher version is more modules — and guessing it left the code
 * overlapping whatever came next. `measure` defaults to an estimate; `drawSlip`
 * passes the real one.
 */
export function measureHeight(slip: Slip, base: number, dots: number, measure: Measure = estimate): number {
  return layout(slip, base, dots, measure)
}

export interface Raster {
  readonly width: number
  readonly height: number
  /** One bit per pixel, MSB first, row-major — the layout `GS v 0` wants. */
  readonly bits: Uint8Array
}

/** Draw the slip and read it back as 1-bit pixels. */
export function drawSlip(slip: Slip, options: RasterOptions, makeCanvas: CanvasFactory): Raster {
  const base = options.baseSize ?? baseSizeFor(options.dots)
  const family = options.fontFamily ?? RECEIPT_FONT
  const width = options.dots

  // The canvas is measured on before it is sized: text wraps by the font's own
  // widths, so how tall the slip is depends on the face, and resizing a canvas
  // afterwards is free where allocating a guess and cropping is not.
  const { context, done } = makeCanvas(width, 1)
  const measure: Measure = (text, px, weight) => {
    context.font = `${weight} ${px}px ${family}`
    return context.measureText(text).width
  }
  const height = layout(slip, base, width, measure)
  context.canvas.height = height

  context.fillStyle = '#fff'
  context.fillRect(0, 0, width, height)
  context.fillStyle = '#000'
  context.textBaseline = 'top'

  layout(slip, base, width, measure, {
    text: (text, x, y, align, px, weight) => {
      context.font = `${weight} ${px}px ${family}`
      context.textAlign = align
      context.fillText(text, x, y)
    },
    rect: (x, y, w, h) => context.fillRect(x, y, w, h),
    image: (data, x, y, w, h) => {
      const image = options.images?.get(data)
      // No decoded image is not an error here — see `RasterOptions.images`.
      // The space is reserved either way, so nothing below this line moves.
      if (image !== undefined && context.drawImage !== undefined) context.drawImage(image, x, y, w, h)
    }
  })

  const image = context.getImageData(0, 0, width, height)
  done()
  return { width, height, bits: toBits(image.data, width, height) }
}

/** Threshold to 1bpp. Anything darker than mid-grey is ink. */
export function toBits(rgba: Uint8ClampedArray, width: number, height: number): Uint8Array {
  const bytesPerRow = Math.ceil(width / 8)
  const bits = new Uint8Array(bytesPerRow * height)
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = (y * width + x) * 4
      const luma = (rgba[i]! * 299 + rgba[i + 1]! * 587 + rgba[i + 2]! * 114) / 1000
      if (luma < 128) bits[y * bytesPerRow + (x >> 3)]! |= 0x80 >> (x & 7)
    }
  }
  return bits
}

/**
 * `GS v 0` — print a raster bit image.
 *
 * Sent in bands rather than as one image: a full slip is tens of kilobytes and
 * a small printer buffer on a link with no flow control — serial, raw TCP —
 * drops whatever arrives after it fills, printing most of a receipt and no cut.
 *
 * Every band boundary costs something, though: the printer stops its motor at
 * the end of an image and the paper slips when it starts again, which is a white
 * stripe across whatever text the boundary fell through. So a boundary is moved
 * up to the last blank row in the band, where a stripe is a slightly
 * wider gap between lines, and a link that has flow control is sent bands as tall
 * as the command allows — see `BAND_ROWS_FLOW_CONTROLLED`.
 */
export function toEscPosRaster(raster: Raster, bandHeight = 128): Uint8Array {
  const bytesPerRow = Math.ceil(raster.width / 8)
  const chunks: Uint8Array[] = [Uint8Array.from([0x1b, 0x40])]

  for (let top = 0; top < raster.height; ) {
    const rows = bandRows(raster, bytesPerRow, top, bandHeight)
    const header = Uint8Array.from([
      0x1d,
      0x76,
      0x30,
      0x00,
      bytesPerRow & 0xff,
      (bytesPerRow >> 8) & 0xff,
      rows & 0xff,
      (rows >> 8) & 0xff
    ])
    chunks.push(header, raster.bits.subarray(top * bytesPerRow, (top + rows) * bytesPerRow))
    top += rows
  }

  chunks.push(Uint8Array.from([0x1b, 0x64, 0x03]), Uint8Array.from([0x1d, 0x56, 0x01]))
  return concatBytes(chunks)
}

/**
 * The tallest band worth sending down a link that has flow control — USB, where
 * a full printer stops accepting rather than dropping.
 *
 * Not the whole slip: Epson documents 2303 rows as the most one `GS v 0` image
 * may have on several models, and a long order is taller than that.
 */
export const BAND_ROWS_FLOW_CONTROLLED = 2048

/** How many rows the band starting at `top` takes, ending on a blank row when one is near. */
function bandRows(raster: Raster, bytesPerRow: number, top: number, bandHeight: number): number {
  const rows = Math.min(bandHeight, raster.height - top)
  if (top + rows === raster.height) return rows
  // Any blank row will do, however short the band it leaves: an extra motor
  // stop in a gap between lines costs nothing, one through a QR costs a scan.
  for (let end = rows; end > 0; end -= 1) {
    if (isBlankRow(raster.bits, bytesPerRow, top + end - 1)) return end
  }
  return rows
}

function isBlankRow(bits: Uint8Array, bytesPerRow: number, y: number): boolean {
  for (let x = y * bytesPerRow, end = x + bytesPerRow; x < end; x += 1) if (bits[x] !== 0) return false
  return true
}

export { row }
